import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Send, Copy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const emptyItem = { description: "", quantity: 1, rate: 0 };
const emptyForm = { client_name: "", client_email: "", items: [{ ...emptyItem }], tax_rate: 0, due_date: "", notes: "" };

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setItem = (i, k, num) => (e) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: num ? Number(e.target.value) : e.target.value };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { ...emptyItem }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((s, i) => s + i.quantity * i.rate, 0);
  const total = subtotal * (1 + Number(form.tax_rate) / 100);

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/invoices", { ...form, tax_rate: Number(form.tax_rate) }); toast.success("Invoice created"); setOpen(false); setForm(emptyForm); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const send = async (id) => { try { await api.post(`/invoices/${id}/send`); toast.success("Invoice emailed to client"); load(); } catch (e) { toast.error("Failed to send"); } };
  const del = async (id) => { await api.delete(`/invoices/${id}`); toast.success("Deleted"); load(); };
  const copyLink = (id) => { navigator.clipboard.writeText(`${window.location.origin}/pay/${id}`); toast.success("Payment link copied"); };

  const badge = (s) => ({ paid: "bg-green-100 text-green-700", sent: "bg-blue-100 text-blue-700", draft: "bg-brand-mist text-brand-ink/60" }[s] || "bg-brand-mist");

  return (
    <div data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-4xl">Invoices</h1><p className="text-brand-ink/60 mt-2">Bill clients and collect payments online.</p></div>
        <button onClick={() => { setForm(emptyForm); setOpen(true); }} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="add-invoice-btn"><Plus className="w-4 h-4" /> New invoice</button>
      </div>

      <div className="mt-8 bg-white rounded-2xl border border-brand-line overflow-hidden">
        {invoices.length === 0 ? <div className="p-8 text-brand-ink/50" data-testid="invoices-empty">No invoices yet.</div> : (
          <div className="divide-y divide-brand-line">
            {invoices.map((inv) => (
              <div key={inv.id} className="p-5 flex items-center justify-between flex-wrap gap-4" data-testid={`invoice-${inv.id}`}>
                <div>
                  <div className="font-medium flex items-center gap-3"><span className="font-display tracking-widest">{inv.number}</span><span className={`text-xs uppercase tracking-widest px-3 py-1 rounded-full ${badge(inv.status)}`} data-testid={`invoice-status-${inv.id}`}>{inv.status}</span></div>
                  <div className="text-sm text-brand-ink/50 mt-1">{inv.client_name} · {inv.client_email}</div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-display text-2xl">${inv.total.toFixed(2)}</span>
                  <button onClick={() => copyLink(inv.id)} className="text-brand-ink/40 hover:text-brand-red transition-colors" title="Copy pay link" data-testid={`copy-link-${inv.id}`}><Copy className="w-4 h-4" /></button>
                  <button onClick={() => send(inv.id)} className="inline-flex items-center gap-1.5 text-sm text-brand-ink hover:text-brand-red transition-colors" data-testid={`send-invoice-${inv.id}`}><Send className="w-4 h-4" /> Send</button>
                  <button onClick={() => del(inv.id)} className="text-brand-ink/30 hover:text-brand-red transition-colors" data-testid={`delete-invoice-${inv.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="invoice-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto p-8">
            <div className="flex items-center justify-between mb-6"><h2 className="font-display text-2xl">New invoice</h2><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Fld label="Client name" v={form.client_name} on={set("client_name")} req testid="inv-client-name" />
                <Fld label="Client email" type="email" v={form.client_email} on={set("client_email")} req testid="inv-client-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">Line items</label>
                <div className="mt-2 space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`inv-item-row-${i}`}>
                      <input placeholder="Description" value={it.description} onChange={setItem(i, "description")} required className="flex-1 rounded-lg border border-brand-line px-3 py-2 text-sm focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={`inv-item-desc-${i}`} />
                      <input type="number" placeholder="Qty" value={it.quantity} onChange={setItem(i, "quantity", true)} className="w-20 rounded-lg border border-brand-line px-3 py-2 text-sm focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={`inv-item-qty-${i}`} />
                      <input type="number" placeholder="Rate" value={it.rate} onChange={setItem(i, "rate", true)} className="w-24 rounded-lg border border-brand-line px-3 py-2 text-sm focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={`inv-item-rate-${i}`} />
                      {form.items.length > 1 && <button type="button" onClick={() => rmItem(i)} className="text-brand-ink/30 hover:text-brand-red"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addItem} className="mt-2 text-sm text-brand-red hover:underline" data-testid="inv-add-item">+ Add item</button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Fld label="Tax rate (%)" type="number" v={form.tax_rate} on={set("tax_rate")} testid="inv-tax" />
                <Fld label="Due date" type="date" v={form.due_date} on={set("due_date")} testid="inv-due" />
              </div>
              <div className="flex justify-end gap-6 text-sm border-t border-brand-line pt-4">
                <span className="text-brand-ink/60">Subtotal: ${subtotal.toFixed(2)}</span>
                <span className="font-display text-xl" data-testid="inv-total-preview">Total: ${total.toFixed(2)}</span>
              </div>
              <button type="submit" className="w-full px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="inv-save-btn">Create invoice</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Fld({ label, type = "text", v, on, req, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">{label}</label>
      <input type={type} value={v} onChange={on} required={req} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={testid} />
    </div>
  );
}
